import { AppThunk, RootState } from 'app/store';
import flatten from 'lodash/flatten';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import pick from 'lodash/pick';
import pull from 'lodash/pull';
import sortBy from 'lodash/sortBy';
import { DateTime } from 'luxon';
import FragnetService from 'services/FragnetService';

import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

// Types

export interface QueryParams {
  [key: string]: number | null;
  hac: number;
  rac: number;
  hops: number;
  groupLimit: number | null;
}

export type PropertyTypes = number; // | string

export interface SearchResultsNodeProperties {
  [key: string]: PropertyTypes;
}

export interface SearchResultsNode {
  id: number;
  props: SearchResultsNodeProperties;
}

export interface SearchResultsEdge {
  id: number;
  parentId: number;
  childId: number;
  label: string;
}

export interface SearchResultsGroupMember {
  id: number;
  smiles: string;
  calculations: SearchResultsNodeProperties;
}

export interface SearchResultsGroup {
  id?: string;
  classification: string;
  prototype: string;
  refmolAtomsMissing: number;
  memberCount: number;
  members: SearchResultsGroupMember[];
}

interface IMSearchResult {
  parameters: QueryParams;
  refmol: string;
  groupCount: number;
  groups: SearchResultsGroup[];
  nodes?: SearchResultsNode[];
}

export interface SearchMoleculeResponseData extends IMSearchResult {
  apiVersion: string;
  query: string;
  resultAvailableAfter: number;
  processingTime: number;
}

export interface SearchResult extends IMSearchResult {
  isFavourite: boolean; // Added for saving favourites
  molFile: string;
  timeOfResultAcquisition: number;
  calculations: string[];
}

export interface SearchMoleculesState {
  isQueryLoading: boolean;
  searchResults: SearchResult[];
  selectedResultNum?: number;
}

interface RestructureParams {
  responseData: SearchMoleculeResponseData;
  molFile: string;
  timeOfResultAcquisition: number;
  parameters: QueryParams;
  calculations: string[];
}

type SuccessPayload = { result: SearchResult; molFile: string };

// Constants

const PROPERTIES_BLACKLIST = ['osmiles', 'chac', 'hac', 'cmpd_ids'];

// Slice

const initialState: SearchMoleculesState = {
  isQueryLoading: false,
  searchResults: [],
  selectedResultNum: 0,
};

export const searchMoleculesSlice = createSlice({
  name: 'searchMolecules',
  initialState,
  reducers: {
    searchMoleculeLoading(state) {
      state.isQueryLoading = true;
    },
    searchMoleculeSuccess: {
      reducer(state, action: PayloadAction<SuccessPayload>) {
        state.isQueryLoading = false;
        state.searchResults.unshift(action.payload.result);
        state.selectedResultNum = 0;

        const notFavourite = state.searchResults.filter((result) => !result.isFavourite);
        if (state.searchResults.length > 10) {
          // remove the last value that is not a favourite from the state
          pull(state.searchResults, notFavourite.pop());
        }
      },
      prepare({
        responseData,
        molFile,
        timeOfResultAcquisition,
        parameters,
        calculations,
      }: RestructureParams) {
        let properties: {
          [key: number]: SearchResultsNodeProperties;
        } = {};

        responseData.nodes?.forEach((node) => {
          let props = omit(node.props, PROPERTIES_BLACKLIST);
          props = omitBy(props, (value) => typeof value !== 'number');
          properties[node.id] = props;
        });

        const groups = responseData.groups.map((group) => ({
          ...pick(group, ['classification', 'id', 'refmolAtomsMissing', 'memberCount']),
          prototype: group.prototype,
          members: group.members.map((member) => ({
            ...pick(member, ['id', 'smiles']),
            calculations: properties[member.id] ?? {},
          })),
        }));

        return {
          payload: {
            result: {
              ...pick(responseData, ['groupCount', 'refmol', 'nodes']),
              groups,
              molFile,
              timeOfResultAcquisition,
              isFavourite: false,
              parameters,
              calculations,
            },
            molFile,
          },
        };
      },
    },
    searchMoleculeFailure(state) {
      state.isQueryLoading = false;
    },
    changeSelectedResultNum(state, action: PayloadAction<number>) {
      state.selectedResultNum = action.payload;
    },
    toggleFavourite(state, action: PayloadAction<number>) {
      const result = state.searchResults[action.payload];
      result.isFavourite = !result.isFavourite;
    },
    removeResult(state, action: PayloadAction<number>) {
      state.searchResults.splice(action.payload, 1);

      if (action.payload === state.selectedResultNum) {
        const latestResult = sortBy(state.searchResults, ['timeOfResultAcquisition']).pop();
        if (latestResult === undefined) {
          state.selectedResultNum = latestResult;
        } else {
          state.selectedResultNum = state.searchResults.indexOf(latestResult);
        }
      } else if (state.selectedResultNum && action.payload < state.selectedResultNum) {
        state.selectedResultNum = state.selectedResultNum - 1;
      }
    },
  },
});

// Thunk Actions
export const searchMoleculesThunk = (
  smiles: string,
  queryParams: QueryParams,
  calculations: string[],
  molFile: string,
): AppThunk<Promise<SuccessPayload>> => async (dispatch): Promise<SuccessPayload> => {
  const {
    searchMoleculeLoading,
    searchMoleculeSuccess,
    searchMoleculeFailure,
  } = searchMoleculesSlice.actions;
  dispatch(searchMoleculeLoading());
  try {
    const neighbourMolecules = await FragnetService.searchNeighbourhood(smiles, {
      ...queryParams,
      calculations,
    });
    return dispatch(
      searchMoleculeSuccess({
        responseData: neighbourMolecules.data,
        molFile,
        timeOfResultAcquisition: DateTime.utc().ts,
        parameters: queryParams,
        calculations,
      }),
    ).payload;
  } catch (error) {
    dispatch(searchMoleculeFailure());
    throw error;
  }
};

// Selectors
export const searchMoleculesStateSelector = (state: RootState, ..._: any): SearchMoleculesState =>
  state.searchMolecules;

export const getIsQueryLoading = createSelector(
  searchMoleculesStateSelector,
  (state) => state.isQueryLoading,
);
export const getSearchResults = createSelector(
  searchMoleculesStateSelector,
  (state) => state.searchResults,
);
export const getSearchResultsCount = createSelector(
  searchMoleculesStateSelector,
  (state) => state.searchResults.length,
);
export const getSelectedResultNum = createSelector(
  searchMoleculesStateSelector,
  (state) => state.selectedResultNum,
);
export const getSelectedResult = createSelector(
  getSearchResults,
  getSelectedResultNum,
  (results, selectedResultNum) =>
    selectedResultNum === undefined ? undefined : results[selectedResultNum],
);

// Number of members in the selected result in the actual response
export const getSelectedResultMemberCount = createSelector(getSelectedResult, (selectedResult) =>
  selectedResult?.groups.map((group) => group.members.length).reduce((a, b) => a + b),
);
export const getAllMembers = createSelector(searchMoleculesStateSelector, (state) => {
  const groups = flatten(state.searchResults.map((result) => result.groups));
  return flatten(groups.map((group) => group.members));
});
export const getMembersFromSelectedResult = createSelector(getSelectedResult, (selectedResult) =>
  flatten(selectedResult?.groups.map((group) => group.members)),
);

export const {
  searchMoleculeLoading,
  searchMoleculeSuccess,
  searchMoleculeFailure,
  changeSelectedResultNum,
  toggleFavourite,
  removeResult,
} = searchMoleculesSlice.actions;

export const actions = {
  ...searchMoleculesSlice.actions,
  searchMoleculesThunk,
};
export const { reducer } = searchMoleculesSlice;

export default searchMoleculesSlice.reducer;
